import { takeLatest, call, put } from 'redux-saga/effects';

import api from '../api';
import { Part } from '../vehicles/actions';
import {
    listPartsRequest,
    listPartsSuccess,
    listPartsFailure,
    updatePartsRequest,
    updatePartsSuccess,
    updatePartsFailure,
    listPartsDeliveryRequest,
    listPartsDeliverySuccess,
    listPartsDeliveryFailure,
    getPartRequest,
    getPartSuccess,
    getPartFailure,
} from './actions';

// API calls
const listPartApi = async (filters: string, part_fields: string, excludes: string): Promise<Part[]> => {
    let params = new URLSearchParams({
        filters: filters || '',
        excludes: excludes || '',
        part_fields: part_fields
    }).toString();

    const response = await api.get('/main/api/parts/list/?' + params);
    return response.data;
};

const listPartDeliveryApi = async (date_from: string, date_to: string): Promise<Part[]> => {
    const response = await api.get('/main/api/vehicles/deliveries/?date_from=' + date_from + '&date_to=' + date_to);
    return response.data;
};

const updatePartsApi = async (parts: Part[], update_fields: string[], part_fields?: string): Promise<Part[]> => {
    let params = "";
    if (part_fields) {
        params = "?"+ new URLSearchParams({
            part_fields: part_fields
        }).toString();
    };

    let data = Object.values(parts).map((el) => Object.fromEntries(Object.entries(el).filter(([key]) => update_fields.includes(key))));

    const response = await api.post('/main/api/parts/post/' + params, data);
    return response.data;
};

// Sagas
function* handleListPart(action: ReturnType<typeof listPartsRequest>) {
    try {
        const data: Part[] = yield call(
            listPartApi,
            action.payload.filters,
            action.payload.part_fields,
            action.payload.excludes
        );
        yield put(listPartsSuccess({data: data, save_as: action.payload?.save_as || 'parts'}));
    } catch (error: any) {
        yield put(listPartsFailure(error.response?.data || 'Failed to list parts'));
    }
}

function* handleListPartDelivery(action: ReturnType<typeof listPartsDeliveryRequest>) {
    try {
        const data: Part[] = yield call(
            listPartDeliveryApi,
            action.payload.date_from || '',
            action.payload.date_to || ''
        );
        yield put(listPartsDeliverySuccess(data));
    } catch (error: any) {
        yield put(listPartsDeliveryFailure(error.response?.data || 'Failed to list parts'));
    }
}

function* handleUpdateParts(action: ReturnType<typeof updatePartsRequest>) {
    try {
        const data: Part[] = yield call(
            updatePartsApi,
            action.payload.parts,
            action.payload.update_fields,
            action.payload.part_fields,
        );
        yield put(updatePartsSuccess({data: data, save_as: action.payload.save_as || 'parts'}));
    } catch (error: any) {
        yield put(updatePartsFailure(error.response?.data || 'Failed to update parts'));
    }
}

function* handleGetPart(action: ReturnType<typeof getPartRequest>) {
    try {
        const filters = 'id:' + action.payload.id;
        const data: Part[] = yield call(
            listPartApi,
            filters,
            action.payload.part_fields,
            ''
        );
        yield put(getPartSuccess(data.length ? data[0] : {id: -1}));
    } catch (error: any) {
        yield put(getPartFailure(error.response?.data || 'Failed to get part'));
    }
}

// Watcher sagas
export function* watchPartsSaga() {
    yield takeLatest(listPartsRequest.type, handleListPart); // Listen for list parts request
    yield takeLatest(listPartsDeliveryRequest.type, handleListPartDelivery); // Listen for list parts request
    yield takeLatest(updatePartsRequest.type, handleUpdateParts);
    yield takeLatest(getPartRequest.type, handleGetPart);
}
